import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../http';

/**
 * Thin wrapper around useQuery for GET requests.
 * Drop-in for the manual useApi hook with React Query's caching + deduplication.
 *
 * Usage:
 *   const { data, isLoading, error, refetch } = useQueryApi('/api/admin/workflow-steps');
 *
 * @param {string|null} url - API path. Pass null to skip.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options]
 */
export function useQueryApi(url, options = {}) {
  return useQuery({
    queryKey: [url],
    queryFn: async () => {
      const res = await api.get(url);
      return res.data;
    },
    enabled: !!url,
    ...options
  });
}

/**
 * Wrapper around useMutation for POST/PUT/DELETE requests.
 * Automatically invalidates related queries on success.
 *
 * Usage:
 *   const { mutate: dispatch } = useMutationApi('/api/test-sessions/dispatch', {
 *     invalidates: ['/api/test-events/1/full-dashboard']
 *   });
 *   dispatch({ company_id: 1, station_id: 2 });
 *
 * @param {string} url
 * @param {{ method?: string, invalidates?: string[] } & import('@tanstack/react-query').UseMutationOptions} [options]
 */
export function useMutationApi(url, { method = 'post', invalidates = [], ...mutationOptions } = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => api[method](url, data).then((r) => r.data),
    onSuccess: () => {
      invalidates.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    },
    ...mutationOptions
  });
}
